import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getUpcomingCallRemindersApi } from "./calls-api";

// Client-side call reminders. The backend has no notification pipeline for
// CallReminder rows (no cron/email/push) — they're stored on save and
// otherwise inert. This polls upcoming calls (next 24h) while the CRM tab
// is open and surfaces each configured offset before start_time as both an
// in-app modal (always visible, no OS/browser permission dependency) and a
// browser Notification (best-effort, requires permission + OS not blocking
// it — e.g. Focus Assist). Neither survives the tab being closed.
const POLL_INTERVAL_MS = 30 * 1000;

const formatOffsetLabel = (minutes) =>
  minutes >= 60 ? `${minutes / 60} hour${minutes > 60 ? "s" : ""}` : `${minutes} mins`;

const CallReminderNotifier = () => {
  const navigate = useNavigate();
  // Tracks which "call:offset" pairs have already fired, so re-polling the
  // same upcoming call doesn't re-notify. Kept in a ref (not state) since it
  // never needs to trigger a re-render.
  const firedRef = useRef(new Set());
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const showBrowserNotification = (call, minutes, startTime) => {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      try {
        new Notification(`Upcoming call: ${call.title || call.subject || "Call"}`, {
          body: `Starts in ${formatOffsetLabel(minutes)} — ${new Date(startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
          tag: `${call.id}:${minutes}`,
        });
      } catch (error) {
        console.error("Browser notification failed:", error);
      }
    };

    const checkReminders = async () => {
      try {
        const response = await getUpcomingCallRemindersApi();
        const calls = response?.data?.calls || [];
        const now = Date.now();

        calls.forEach((call) => {
          const startTime = new Date(call.start_time.replace(" ", "T")).getTime();
          if (Number.isNaN(startTime)) return;

          (call.reminder_minutes || []).forEach((minutes) => {
            const fireAt = startTime - minutes * 60 * 1000;
            const key = `${call.id}:${minutes}`;

            // Fire once the reminder time has passed but only within a
            // reasonable catch-up window (10 min) so a reminder that was
            // already well in the past when the tab opened doesn't spam a
            // popup for something long over.
            if (now >= fireAt && now - fireAt <= 10 * 60 * 1000 && !firedRef.current.has(key)) {
              firedRef.current.add(key);
              setQueue((prev) => [
                ...prev,
                { key, id: call.id, title: call.title || call.subject || "Call", startTime, minutes },
              ]);
              showBrowserNotification(call, minutes, startTime);
            }
          });
        });
      } catch (error) {
        // Silent — reminders are a best-effort convenience, not critical path.
        console.error("Failed to check call reminders:", error);
      }
    };

    const start = () => {
      if (cancelled) return;
      checkReminders();
      intervalId = setInterval(checkReminders, POLL_INTERVAL_MS);
    };

    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(start);
    } else {
      start();
    }

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  const current = queue[0];

  const dismiss = () => setQueue((prev) => prev.slice(1));

  const viewCall = () => {
    if (current) navigate("/calls/view", { state: { id: current.id } });
    dismiss();
  };

  if (!current) return null;

  return (
    <Modal show centered onHide={dismiss}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "18px" }}>Call Reminder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-1" style={{ fontWeight: 600, fontSize: "16px" }}>
          {current.title}
        </p>
        <p className="mb-0 text-muted">
          Starts in {formatOffsetLabel(current.minutes)} —{" "}
          {new Date(current.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={dismiss}>
          Dismiss
        </Button>
        <Button variant="primary" onClick={viewCall}>
          View Call
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CallReminderNotifier;
